/**
 * A plant's shifts from its own shift calendar (GET /v1/twin/plant-time; the server's shift_calendar.go), for a heat
 * that carries no recorded shift. The platform never invents a shift: with no calendar there is none, the server's
 * rule and now the web's. The web used to cut a heat into Morning / Afternoon / Night by the VIEWER's browser hour,
 * which gave a two-shift plant three invented shifts, cut in the viewer's zone (the CWC walk, 2026-09-26). Pure.
 */

import { api } from "@/lib/api-client";

/** One shift's start ("HH:MM", local to the plant's zone) and the plant's own name for it. */
export interface ShiftBoundary {
  start: string;
  label: string;
  /**
   * When the shift's work ends ("HH:MM", local), optional (2026-10-19): the gateways fetch update packages outside, or
   * inside, the shifts by it. It never moves a shift boundary: a shift still runs to the next shift's start.
   */
  end?: string;
}

/** The org's plant time as GET /v1/twin/plant-time returns it. */
export interface PlantTime {
  timezone: string;
  shift_calendar: ShiftBoundary[];
  production_day_start: string;
  /**
   * The plant's week start, an ISO weekday (1 Monday .. 7 Sunday) as orgs.production_week_start stores it, which the
   * digest and the briefs cut a plant week from; null when the plant named none. A server from before the route
   * returned it sends none; either way a week then starts on Monday, the server's default, and is said to
   * (plantWeekStartOf).
   */
  production_week_start?: number | null;
}

/**
 * PUT /v1/twin/plant-time's body: each field it names is set, and each it leaves out keeps what is stored, so a page
 * that sends only what changed never wipes the shift calendar. An explicit empty value clears its field: [] the
 * calendar, "" the zone or the day start, null the week start (Monday).
 */
export interface PlantTimeChange {
  timezone?: string;
  production_day_start?: string;
  shift_calendar?: ShiftBoundary[];
  production_week_start?: number | null;
}

/** The PUT's answer: the plant time as stored now, and what the change applies to. */
export interface PlantTimeSaved {
  plant_time: PlantTime;
  note: string;
}

/** A plant week's first day, an ISO weekday (1 Monday .. 7 Sunday), and whether it is the plant's own week start. */
export interface PlantWeekStart {
  weekday: number;
  plant: boolean;
}

/**
 * The day a plant week starts on: the plant's own week start when its plant time carries one that reads (an integer
 * from 1 to 7, as the server stores it), else Monday, which is not the plant's own but the server's default.
 */
export function plantWeekStartOf(pt: PlantTime | null | undefined): PlantWeekStart {
  const n = pt?.production_week_start;
  return typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 7
    ? { weekday: n, plant: true }
    : { weekday: 1, plant: false };
}

export const plantTimeApi = {
  get: () => api.get<PlantTime>("/v1/twin/plant-time"),
  /** Org admins: sets what the change names and keeps the rest. */
  put: (change: PlantTimeChange) => api.put<PlantTimeSaved>("/v1/twin/plant-time", change),
};

/** A readable calendar: the plant's zone, and its shifts' starts (minutes past local midnight) in day order. */
export interface ShiftCalendar {
  timezone: string;
  starts: number[];
  labels: string[];
}

/**
 * A shift start as minutes past midnight, read as the server reads it (plantclock.ParseHHMM): two whole numbers either
 * side of one colon, the hour 0 to 23 and the minute 0 to 59, spaces around the whole trimmed. "6:00" is 06:00: the
 * server stores a start as the plant wrote it, so a calendar written with a one-digit hour reads here as it does there.
 * null for anything else (a calendar is never guessed at).
 */
export function parseHHMM(s: string): number | null {
  const parts = (s ?? "").trim().split(":");
  if (parts.length !== 2 || !parts.every((p) => /^[+-]?\d+$/.test(p))) return null;
  const h = Number(parts[0]);
  const min = Number(parts[1]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min || 0; // never -0, which "-0:00" would give
}

/** A day start lies within 12 hours of the midnight of its date, either way: a plant day always holds its own noon. */
const DAY_START_LIMIT = 12 * 60;

/** Minutes as "HH:MM", zero-padded. */
function twoDigits(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * A production day start as the server reads it (plantclock.ParseDayStart): signed minutes from the midnight that
 * begins the date a plant day carries. "HH:MM" (00:00 to 11:59) starts the day that long after it; "-HH:MM" (-11:59 to
 * -00:01) that long before it, on the evening before, and the day carries the next date ("-01:30": the night shift
 * starting 22:30 counts toward the next date). A leading "+" is the plain form. null for anything else, and for none
 * (which a plant clock reads as midnight). The shared vectors in tools/forge-extract/tests/vectors/plantclock hold the
 * server and this to the same reading.
 */
export function parseDayStart(s: string | null | undefined): number | null {
  const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec((s ?? "").trim());
  if (!m) return null;
  const h = Number(m[2]);
  const min = Number(m[3]);
  if (h > 23 || min > 59) return null;
  const v = h * 60 + min;
  if (m[1] === "-" && v === 0) return null; // midnight is 00:00
  const signed = m[1] === "-" ? -v : v;
  return signed > -DAY_START_LIMIT && signed < DAY_START_LIMIT ? signed : null;
}

/** Signed day-start minutes written as the server stores them: "03:00", or "-01:30" for 22:30 the evening before. */
export function formatDayStart(minutes: number): string {
  return `${minutes < 0 ? "-" : ""}${twoDigits(Math.abs(minutes))}`;
}

/** The time on the plant's wall clock a day start falls at: "22:30" for -90 (on the evening before), "03:00" for 180. */
export function dayStartWallClock(minutes: number): string {
  return twoDigits(((minutes % 1440) + 1440) % 1440);
}

/**
 * When the plant's production day starts, in words: "03:00", or "22:30 the evening before" for "-01:30"; "00:00" when
 * none is set. null for one that does not read.
 */
export function dayStartWords(s: string | null | undefined): string | null {
  if (!(s ?? "").trim()) return "00:00";
  const m = parseDayStart(s);
  if (m === null) return null;
  return m < 0 ? `${dayStartWallClock(m)} the evening before` : dayStartWallClock(m);
}

/** A plant's clock as the server reads it: its zone, and its day start in signed minutes (parseDayStart). */
export interface PlantClock {
  zone: string;
  start: number;
}

/**
 * The plant's clock as the server reads it (loadPlantClock), or null for UTC days: no zone, a zone the browser does
 * not know, or a day start that does not read, which the server reads as no clock at all (never as midnight). No day
 * start is midnight.
 */
export function plantClockOf(pt: PlantTime | null | undefined): PlantClock | null {
  const zone = pt?.timezone?.trim();
  if (!zone || !wallClock(zone)) return null;
  const raw = (pt?.production_day_start ?? "").trim();
  if (!raw) return { zone, start: 0 };
  const start = parseDayStart(raw);
  return start === null ? null : { zone, start };
}

/**
 * The plant's calendar, or null when it has none it can use: no zone, no shifts, a start that does not read, a zone
 * the browser does not know, or two shifts starting at the same time.
 */
export function shiftCalendarOf(pt: PlantTime | null | undefined): ShiftCalendar | null {
  const tz = pt?.timezone?.trim();
  const shifts = pt?.shift_calendar ?? [];
  if (!tz || shifts.length === 0) return null;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
  } catch {
    return null;
  }
  const parsed: { start: number; label: string }[] = [];
  for (const s of shifts) {
    const start = parseHHMM(s.start);
    const label = s.label?.trim();
    if (start === null || !label) return null;
    parsed.push({ start, label });
  }
  parsed.sort((a, b) => a.start - b.start);
  if (parsed.some((p, i) => i > 0 && p.start === parsed[i - 1].start)) return null;
  return { timezone: tz, starts: parsed.map((p) => p.start), labels: parsed.map((p) => p.label) };
}

/** Minutes past local midnight of an instant in a zone. */
function localMinutes(t: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(t);
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return h * 60 + m;
}

/**
 * The shift an instant falls in on the plant's calendar, as the server's shiftAt names it: the latest shift starting at
 * or before the instant's local time, and before the first start the last shift (it wraps midnight). null for a time
 * that does not read.
 */
export function shiftAt(cal: ShiftCalendar, iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const m = localMinutes(t, cal.timezone);
  for (let i = cal.starts.length - 1; i >= 0; i--) {
    if (m >= cal.starts[i]) return cal.labels[i];
  }
  return cal.labels[cal.labels.length - 1];
}

/**
 * A zone's wall-clock formatter, built once per zone: building an Intl.DateTimeFormat costs about twenty times more
 * than using one, and a report reads a plant day for every reading it buckets. null for a zone the browser does not
 * know.
 */
const wallClocks = new Map<string, Intl.DateTimeFormat | null>();
function wallClock(zone: string): Intl.DateTimeFormat | null {
  if (!wallClocks.has(zone)) {
    try {
      wallClocks.set(
        zone,
        new Intl.DateTimeFormat("en-GB", {
          timeZone: zone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        }),
      );
    } catch {
      wallClocks.set(zone, null);
    }
  }
  return wallClocks.get(zone) ?? null;
}

/**
 * The plant day (YYYY-MM-DD) an instant falls on, as the server's plant clock names it (sqlPlantDay): its local time in
 * the plant's zone less the production day's start, as a date; a day that starts the evening before ("-01:30") puts
 * its night shift on the next date. With no clock the server reads (plantClockOf), the UTC day, as the server reads an
 * unconfigured plant. null for a time that does not read.
 */
export function plantDayOf(pt: PlantTime | null | undefined, iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const clock = plantClockOf(pt);
  const parts = wallClock(clock?.zone ?? "UTC")!.formatToParts(t);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "NaN");
  // The local wall clock as a UTC instant, less the day's start: its date is the plant day.
  const local = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"));
  if (Number.isNaN(local)) return null;
  return new Date(local - (clock?.start ?? 0) * 60_000).toISOString().slice(0, 10);
}

/**
 * The hour an instant falls in on the plant's wall clock, as "YYYY-MM-DD HH:00": the calendar date and hour there, not
 * the production day (an hour before the day's start reads on the date it falls on). With no zone the browser knows,
 * the UTC hour. null for a time that does not read. On the night the clocks go back, the repeated hour's two passes
 * read alike.
 */
export function plantHourOf(pt: PlantTime | null | undefined, iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const tz = pt?.timezone?.trim();
  const parts = ((tz ? wallClock(tz) : null) ?? wallClock("UTC"))!.formatToParts(t);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:00`;
}
